import { Checkbox } from '@ark-ui/react/checkbox';
import { type Blob } from '@consigli/types';
import clsx from 'clsx';
import { Eye } from 'lucide-react';
import { type FC, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import { AddContextCheckboxControl } from './add-context-checkbox';

const ICON_STROKE_WIDTH = 1.75;

interface AddContextDocumentCardProps {
  document: Blob;
  /** Opens the document full width, over the modal. */
  onPreview: (document: Blob) => void;
  onSelectedChange: (selected: boolean) => void;
  selected: boolean;
}

export const AddContextDocumentCard: FC<AddContextDocumentCardProps> = ({
  document,
  onPreview,
  onSelectedChange,
  selected,
}) => {
  const { t } = useTranslation();
  const previewTooltipId = useId();

  return (
    <div
      className={clsx(
        // Padding moved onto the label below, so the row has none of its own to
        // swallow a click.
        'relative flex w-full shrink-0 items-start rounded-[var(--ds-radius-8)] border border-solid',
        selected
          ? 'border-[var(--ds-color-bg-brand)] bg-[var(--ds-color-bg-secondary)]'
          : 'border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)]',
      )}
    >
      {/*
       * The label carries the row's own padding and fills it, so anywhere on
       * the row toggles - the box, the name, and the empty space around them
       * alike. Padded on the right by the preview control's width and the
       * space either side of it, which is the room the absolutely positioned
       * button below takes out of the flow.
       *
       * The button stays a sibling rather than moving inside here: a control
       * within a <label> would fire and toggle the checkbox on the same click.
       */}
      <Checkbox.Root
        checked={selected}
        onCheckedChange={({ checked }) => onSelectedChange(checked === true)}
        className="flex min-w-0 flex-1 cursor-pointer items-start gap-[var(--ds-space-4)] py-[var(--ds-space-4)] pl-[var(--ds-space-4)] pr-[calc(var(--ds-space-6)+var(--ds-space-4)+var(--ds-space-4))] text-left"
      >
        <Checkbox.HiddenInput />
        <AddContextCheckboxControl />
        {/*
         * The filename alone, extension included. The type had its own line
         * above this, which said the same thing the name already ends with.
         */}
        <span className="min-w-0 flex-1 truncate text-[length:var(--ds-typography-heading-5-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-5-line-height)] text-[var(--ds-color-text-primary)]">
          {document.name}
        </span>
      </Checkbox.Root>
      <button
        type="button"
        aria-label={t('prop-chat.add-context-preview', { name: document.name })}
        data-tooltip-id={previewTooltipId}
        onClick={() => onPreview(document)}
        className="add-context-preview absolute right-[var(--ds-space-6)] top-[var(--ds-space-4)] flex size-[var(--ds-space-4)] shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[var(--ds-color-bg-icon-primary)]"
      >
        <Eye
          aria-hidden="true"
          strokeWidth={ICON_STROKE_WIDTH}
          className="size-[var(--ds-space-4)]"
        />
      </button>
      {/*
       * react-tooltip rather than the design system's, which renders its
       * positioner in place: this row sits inside the list's `overflow-y-auto`,
       * and a non-visible overflow on one axis makes the other clip too, so an
       * in-place tooltip was cut off against the scroll container's edge. This
       * one portals to the body, above the modal's own z-80.
       *
       * The re-skinning is what the chat's own tooltips carry, for the same
       * reason: react-tooltip's stylesheet wins on specificity otherwise.
       */}
      <Tooltip
        id={previewTooltipId}
        place="top"
        content={t('prop-chat.add-context-preview-hint')}
        className="!z-[90] !rounded-[var(--ds-radius-8)] !bg-[var(--ds-color-bg-dark)] !px-[var(--ds-space-2)] !py-[var(--ds-space-1-5)] !font-[var(--ds-typography-font-definitions-font-family-body)] !text-[length:var(--ds-typography-paragraph-13-regular-font-size)] !font-[var(--ds-typography-paragraph-13-regular-font-weight)] !leading-[var(--ds-typography-paragraph-13-regular-line-height)] !tracking-[var(--ds-typography-paragraph-13-regular-letter-spacing)] !text-[var(--ds-color-text-invert)]"
      />
    </div>
  );
};
